import React from "react";
import { useStyle } from "@/context/StyleContext";
import { AvailableTypographyClasses } from "@/types/Typography";

type SupportedHeading = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type SupportedText = "p" | "span";

export type TextProps = {
  children?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  /** override default class for element type (use this only in reusable components to avoid hard coding class names everywhere) */
  fontClassName?: AvailableTypographyClasses;
  uppercase?: boolean;
} & (
  | ({
      type: "a";
      size?: "small" | "medium" | "large";
    } & React.DetailedHTMLProps<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      HTMLAnchorElement
    >)
  | ({
      type?: SupportedHeading;
      size?: "medium";
    } & React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLHeadingElement>,
      HTMLHeadingElement
    >)
  | ({
      type: SupportedText;
      size?: "small" | "medium" | "large";
    } & React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLParagraphElement>,
      HTMLParagraphElement
    >)
);

export const Text = ({
  children,
  type = "p",
  containerStyle,
  fontClassName,
  size = "medium",
  ...textProps
}: TextProps) => {
  const { theme, colors, typographyClasses } = useStyle();
  const selectElement = (children?: React.ReactNode) => {
    const { style, className, ...rest } = textProps as React.DetailedHTMLProps<
      any,
      any
    >;
    let typographyClassName =
      fontClassName || (typographyClasses[type]?.className as any);
    if (className) {
      typographyClassName = `${typographyClassName} ${className}`;
    }

    let fontSize: number | undefined;
    if (size !== "medium") {
      fontSize = size === "small" ? 12 : size === "large" ? 20 : undefined;
    }
    const commonStyles = {
      ...{
        textTransform: textProps.uppercase ? "uppercase" : "none",
        fontSize: fontSize,
      },
      ...style,
    };

    switch (type) {
      case "h1":
        return (
          <h1
            style={{
              ...{ color: colors[theme].heading },
              ...commonStyles,
            }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </h1>
        );
      case "h2":
        return (
          <h2
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </h2>
        );
      case "h3":
        return (
          <h3
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </h3>
        );
      case "h4":
        return (
          <h4
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
          >
            {children}
          </h4>
        );
      case "h5":
        return (
          <h5
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </h5>
        );
      case "h6":
        return (
          <h6
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </h6>
        );
      case "p":
        return (
          <p
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </p>
        );
      case "span":
        return (
          <span
            style={{ ...{ color: colors[theme].text }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </span>
        );
      case "a":
        return (
          <a
            style={{ ...{ color: colors[theme].link }, ...commonStyles }}
            className={typographyClassName}
            {...rest}
          >
            {children}
          </a>
        );
      default:
        return null;
    }
  };
  return <div style={{ ...containerStyle }}>{selectElement(children)}</div>;
};
